import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
 * Modal - dialogi-wrapperi Radix UI Dialogin paalla.
 *
 * Radix tuo:
 *  - portalin ulospain (z-index ei taistele kontekstistaan)
 *  - focus-trapin
 *  - Escape-napin sulkemisen
 *  - klikkaa overlayta -> sulkee
 *  - aria-attribuutit (modal=true, labelledBy=DialogTitle)
 *  - body-scroll-lockin
 *
 * Kayttoesimerkki:
 *
 *   <Modal>
 *     <ModalTrigger asChild>
 *       <Button>Avaa</Button>
 *     </ModalTrigger>
 *     <ModalContent>
 *       <ModalHeader>
 *         <ModalTitle>Vahvista poisto</ModalTitle>
 *         <ModalDescription>Tata ei voi peruuttaa.</ModalDescription>
 *       </ModalHeader>
 *       <ModalFooter>
 *         <ModalClose asChild><Button variant="outline">Peruuta</Button></ModalClose>
 *         <Button variant="destructive">Poista</Button>
 *       </ModalFooter>
 *     </ModalContent>
 *   </Modal>
 *
 * Tai kontrolloituna: kayta open + onOpenChange propsia Modal-juurikomponentissa.
 */

export const Modal = DialogPrimitive.Root;
export const ModalTrigger = DialogPrimitive.Trigger;
export const ModalClose = DialogPrimitive.Close;
export const ModalPortal = DialogPrimitive.Portal;

export const ModalOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function ModalOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-yo/40 backdrop-blur-sm',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

type ModalContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
};

export const ModalContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(function ModalContent({ className, children, showCloseButton = true, ...props }, ref) {
  return (
    <ModalPortal>
      <ModalOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-[calc(100vw-1rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4',
          'max-h-[calc(100vh-1rem)] overflow-y-auto',
          'rounded-lg border border-kaste bg-white p-6 shadow-card-hover',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 rounded-xs p-1 text-usva-t opacity-70',
              'transition-opacity hover:opacity-100 focus-ring',
              'disabled:pointer-events-none',
            )}
            aria-label="Sulje"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </ModalPortal>
  );
});

export function ModalHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function ModalFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const ModalTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function ModalTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-medium text-metsa', className)}
      {...props}
    />
  );
});

export const ModalDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function ModalDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-base text-usva-t', className)}
      {...props}
    />
  );
});
